// frontend/src/pages/TicketDetailPage.jsx
/**
 * TicketDetailPage — single support ticket (P16-G10).
 *
 * Route: /tickets/:id
 *
 * Shows the full ticket (subject, description, status, timestamps) and a
 * "Chat with Aria" prompt that opens the floating chat widget pre-filled with a
 * message referencing this ticket (via the `qrflow:open-chat` window event the
 * ChatWidget listens for).
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  addTicketMessage,
  getTicket,
  resolveTicket,
  reopenTicket,
} from "../api/tickets";
import TicketStatusBadge from "../components/TicketStatusBadge";
import Spinner from "../components/Spinner";

const AUTHOR_LABEL = { user: "You", ai: "QRFlow Assistant", operator: "Support" };

function MessageBubble({ message }) {
  const mine = message.author_type === "user";
  return (
    <div className={mine ? "flex justify-end" : "flex justify-start"}>
      <div
        className={[
          "max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap",
          mine
            ? "bg-blue-600 text-white"
            : message.author_type === "ai"
              ? "bg-indigo-50 text-indigo-900 border border-indigo-100"
              : "bg-gray-100 text-gray-800",
        ].join(" ")}
      >
        <div className="text-[11px] font-semibold opacity-70 mb-0.5">
          {AUTHOR_LABEL[message.author_type] || message.author_type}
        </div>
        {message.body}
      </div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);

  const load = async () => {
    const data = await getTicket(id);
    setTicket(data);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getTicket(id);
        if (active) setTicket(data);
      } catch (err) {
        if (err?.response?.status === 404) {
          if (active) setNotFound(true);
        } else {
          toast.error("Could not load ticket.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const onSendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addTicketMessage(id, reply.trim());
      setReply("");
      await load();
    } catch {
      toast.error("Could not send your reply.");
    } finally {
      setSending(false);
    }
  };

  const onResolve = async (satisfaction) => {
    setResolving(true);
    try {
      await resolveTicket(id, satisfaction);
      await load();
      toast.success("Ticket resolved. Thanks for the feedback!");
    } catch {
      toast.error("Could not resolve the ticket.");
    } finally {
      setResolving(false);
    }
  };

  const onReopen = async () => {
    setResolving(true);
    try {
      await reopenTicket(id);
      await load();
      toast.success("Ticket reopened.");
    } catch {
      toast.error("Could not reopen the ticket.");
    } finally {
      setResolving(false);
    }
  };

  const openChatWithAria = () => {
    window.dispatchEvent(
      new CustomEvent("qrflow:open-chat", {
        detail: {
          agent: "aria",
          message: ticket
            ? `I need help with my support ticket: "${ticket.subject}"`
            : "I need help with a support ticket.",
        },
      }),
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (notFound || !ticket) {
    return (
      <div className="max-w-3xl space-y-4">
        <Link to="/tickets" className="text-sm text-blue-600 hover:underline">
          ← Back to Support
        </Link>
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-12 text-center">
          <p className="text-sm text-gray-500">Ticket not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Link to="/tickets" className="text-sm text-blue-600 hover:underline">
        ← Back to Support
      </Link>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900">{ticket.subject}</h1>
          <TicketStatusBadge status={ticket.status} />
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
          <span>Category: {ticket.category}</span>
          <span>Priority: {ticket.priority}</span>
          <span>Opened: {formatDateTime(ticket.created_at)}</span>
          {ticket.resolved_at && (
            <span>Resolved: {formatDateTime(ticket.resolved_at)}</span>
          )}
        </div>

        {ticket.sla_breached ? (
          <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            ⚠️ This ticket has passed its response SLA — a specialist will reply
            as soon as possible.
          </div>
        ) : ticket.sla_due_at ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            Expected specialist response by {formatDateTime(ticket.sla_due_at)}.
          </div>
        ) : null}
      </div>

      {/* Resolution controls (requester self-close + CSAT, or reopen) */}
      {ticket.status === "resolved" || ticket.status === "closed" ? (
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm text-emerald-800">
            This ticket is {ticket.status}
            {ticket.satisfaction === "positive" && " — you marked it helpful 👍"}
            {ticket.satisfaction === "negative" && " — you marked it not helpful 👎"}
            .
          </p>
          <button
            type="button"
            onClick={onReopen}
            disabled={resolving}
            className="shrink-0 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          >
            Reopen
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Did this resolve your issue?
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onResolve("positive")}
              disabled={resolving}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              👍 Yes, resolved
            </button>
            <button
              type="button"
              onClick={() => onResolve("negative")}
              disabled={resolving}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              👎 Close, but it wasn’t great
            </button>
            <button
              type="button"
              onClick={() => onResolve(null)}
              disabled={resolving}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Just close it
            </button>
          </div>
        </div>
      )}

      {/* Conversation thread */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">Conversation</h2>
        {/* The opening message is the ticket description. */}
        <MessageBubble
          message={{ author_type: "user", body: ticket.description }}
        />
        {(ticket.messages || []).map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}

        <form onSubmit={onSendReply} className="pt-3 border-t border-gray-100 space-y-2">
          <textarea
            aria-label="Reply"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            placeholder="Add a reply…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </form>
      </div>

      {/* Chat-with-Aria prompt */}
      <div className="flex items-center justify-between gap-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
        <p className="text-sm text-blue-800">
          Chat with Aria to get help with this ticket.
        </p>
        <button
          type="button"
          onClick={openChatWithAria}
          className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          Chat with Aria
        </button>
      </div>
    </div>
  );
}
